import { Add, FilterList, List as ListIcon, ViewList } from '@mui/icons-material'
import { Box, Button, Collapse, Stack, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import React, { useCallback, useEffect, useState } from 'react'
import Searchbar from '../../components/Searchbar/Searchbar'
import { RequestProps } from '../../store/types'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import { useAppDispatch } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import navigation from '../../config/navigation'
import { useNavigate } from 'react-router-dom'
import { useGetUsersQuery } from '../../store/redux/reducers/usersSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import FilterUsersMenu from '../../components/menu/FilterUsersMenu'
import { useSelector } from 'react-redux'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import GenerateReportPopup from '../../components/Popup/GenerateReportPopup'
import ReportType from '../../store/types/enums/ReportType'
import ReportPopupData from '../../store/types/utils/ReportPopupUtil'
import defaultProps from '../../config/defaultProps'
import DataTable from '../../components/Table/DataTable'
import { StyledPageContent } from '../../styles/PageStyles'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import { splitName } from '../../store/utils/usersUtils'
import usePersistedPage from '../../store/utils/usePersistedPage'
import HeaderCountLabel from '../../components/Page/HeaderCountLabel'
import UserTile from './UserTile'
import UserTableRow from './UserTableRow'

const UsersView = () => {
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [selectedData, setSelectedData] = useState<ReportPopupData[]>([])
  const theme = useTheme()
  const [page, setPage] = usePersistedPage(SessionStorageKeys.UsersTablePage)

  const updateSelectedData = useCallback(
    (selectedIds: ReportPopupData[]) => {
      setSelectedData(selectedIds)
    },
    [setSelectedData]
  )

  useEffect(() => {
    dispatch(setPageName(t('page.name.users.users')))
    dispatch(setPageIndex(PageIndex.USERS))
    dispatch(pageHeaderActions.setInitialState())
  })

  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)

  const {
    data: usersResponse,
    refetch,
    isLoading,
  } = useGetUsersQuery({
    ...requestProps,
    employeeStatus: requestProps.employeeStatus || 'ACTIVE,BLOCKED',
  })
  const { data: activeUsersResponse, isLoading: isActiveUsersLoading } = useGetUsersQuery({
    ...defaultProps,
    employeeStatus: 'ACTIVE',
  })

  useEffect(() => {
    setRequestProps(prev => ({
      ...prev,
      page: page,
    }))
  }, [page])

  const [dense, setDense] = useState(true)
  const [isFilteringMenuOpen, setIsFilteringMenuOpen] = useState(false)

  useEffect(() => {
    refetch()
  }, [refetch, requestProps])

  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'))

  const open = useSelector(getSidebarState)

  const handleSearch = (value: string) => {
    const { firstName, lastName } = splitName(value)

    setRequestProps(prevProps => ({
      ...prevProps,
      firstName: lastName ? firstName : undefined,
      lastName: lastName || undefined,
      'query:firstName,lastName': lastName ? undefined : firstName,
      page: 0,
    }))

    setPage(0)
  }

  const columns: Column[] = [
    { id: 'firstName', label: t('page.users.table.employee') },
    { id: 'email', label: t('page.users.table.email') },
    { id: 'phoneNumber', label: t('page.users.table.phone') },
    { id: 'leaderLastNames', label: t('page.users.table.leader') },
    { id: 'employeeStatus', label: t('components.table.header.status') },
    { id: 'action', label: t('page.users.table.action') },
  ]
  const unsortableColumns: Column[] = [
    { id: 'phoneNumber', label: t('page.users.table.phone') },
    { id: 'leaderLastNames', label: t('page.users.table.leader') },
    { id: 'action', label: t('page.users.table.action') },
  ]
  const renderUserTile = (user: Row, _labelId: string, checkbox?: React.ReactNode) => (
    <UserTile user={user} checkbox={checkbox} />
  )

  const renderRow = (user: Row) => <UserTableRow user={user} />

  return (
    <StyledPageContent display={'flex'} flexDirection={'column'} gap={'24px'} color="text.light">
      <Stack direction="row" justifyContent="space-between" alignItems={'center'}>
        <HeaderCountLabel
          label={t('components.subheader.activeUsers')}
          count={activeUsersResponse?.totalElements || 0}
          isLoading={isActiveUsersLoading}
        />
        {isManagement && (
          <StyledButtonPrimary
            variant="contained"
            startIcon={<Add />}
            size="medium"
            onClick={() => navigate(navigation.newUser)}
          >
            {isDownSm ? t('components.button.addUserShort') : t('components.button.addUser')}
          </StyledButtonPrimary>
        )}
      </Stack>

      {isDownSm && (
        <>
          <Stack direction="column" gap={2}>
            <Stack direction="row" gap={2}>
              <Searchbar
                placeholder={t('components.placeholder.search')}
                handleChange={handleSearch}
                aria-label={'Searchbar'}
              />
              {!isDownSm && (
                <ToggleButtonGroup exclusive value={dense} size="small">
                  <ToggleButton value={true} aria-label="dense layout" onClick={() => setDense(true)}>
                    <ListIcon />
                  </ToggleButton>
                  <ToggleButton value={false} aria-label="scattered layout" onClick={() => setDense(false)}>
                    <ViewList />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
            <Stack direction="column" gap={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
              <GenerateReportPopup
                selectedData={selectedData}
                reportType={ReportType.Employees}
                leaderId={currentUser?.id}
              />
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FilterList />}
                className={isFilteringMenuOpen ? 'selected' : ''}
                onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
              >
                {t('page.users.buttons.filter')}
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {isUpLg && (
        <Stack direction="row" gap={2}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterList />}
            className={isFilteringMenuOpen ? 'selected' : ''}
            onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
          >
            {t('page.users.buttons.filter')}
          </Button>
          <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} aria-label="search" />

          <ToggleButtonGroup exclusive value={dense} size="small">
            <ToggleButton value={true} aria-label="dense layout" onClick={() => setDense(true)}>
              <ListIcon />
            </ToggleButton>
            <ToggleButton value={false} aria-label="scattered layout" onClick={() => setDense(false)}>
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>
          <GenerateReportPopup
            selectedData={selectedData}
            reportType={ReportType.Employees}
            leaderId={currentUser?.id}
          />
        </Stack>
      )}
      {!isDownSm && !isUpLg && (
        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={2}>
            <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} />
            <ToggleButtonGroup exclusive value={dense} size="small">
              <ToggleButton value={true} aria-label="dense layout" onClick={() => setDense(true)}>
                <ListIcon />
              </ToggleButton>
              <ToggleButton value={false} aria-label="scattered layout" onClick={() => setDense(false)}>
                <ViewList />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack
            direction={isDownMd && open ? 'column' : 'row-reverse'}
            gap={2}
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            <GenerateReportPopup
              selectedData={selectedData}
              reportType={ReportType.Employees}
              leaderId={currentUser?.id}
            />

            <Button
              variant="contained"
              color="secondary"
              startIcon={<FilterList />}
              className={isFilteringMenuOpen ? 'selected' : ''}
              onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
            >
              {t('page.users.buttons.filter')}
            </Button>
          </Stack>
        </Stack>
      )}

      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilteringMenuOpen ? 2 : 0}
        height="fit-content"
        pb={3}
      >
        {isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'vertical'}>
            <FilterUsersMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}

        {!isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'horizontal'}>
            <FilterUsersMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}

        <DataTable
          unsortableColumns={unsortableColumns}
          renderTileCheckbox={true}
          singleSelection={false}
          updateSelectedData={updateSelectedData}
          columns={columns}
          data={usersResponse?.content || []}
          renderRow={renderRow}
          totalElements={usersResponse?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={'page.users.table.noUserFound'}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
          dense={dense}
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.UsersTableRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.UsersTableRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.UsersTableRows}
          renderTileContent={(user: Row, labelId: string, checkbox?: React.ReactNode) =>
            renderUserTile(user, labelId, checkbox) as React.ReactNode
          }
        />
      </Box>
    </StyledPageContent>
  )
}

export default UsersView
